'use client'

import Link from 'next/link'
import { useId, useState, type ReactNode } from 'react'
import { Ban, Check, Clock, Copy, FileText, Hourglass, Info, LifeBuoy, Loader2, LogIn, Mail, SearchX, TriangleAlert, Users } from 'lucide-react'
import { MarkdownRenderer } from '@/components/ai-chat/markdown-renderer'
import { Button } from '@/components/ui/button'
import { UserText } from '@/components/ui/user-text'
import { formatLearnerDay } from '@/components/user-portal/learner-i18n'
import { useTranslation } from '@/lib/i18n'
import { ASK_FAILURE_KEYS, type AskFailure } from '@/lib/knowledge/ask-errors'
import type { AskTurn } from '@/lib/knowledge/ask-turn'
import {
  isTeamCitation,
  type Citation,
  type DocumentCitation,
  type KnowledgeEscalation,
  type KnowledgeTeamDataset,
  type TeamCitation,
} from '@/lib/knowledge/portal-types'
import { formatMomentIn } from '@/lib/time/zoned-day'
import { cn } from '@/lib/utils'
import { usePageLabel } from './page-label'

/**
 * One answer, as the person sees it (K1 contract, "Web — portal"): the text as
 * markdown through the admin chat's renderer, each `[S1]` marker a small
 * numbered chip, the cited sources as cards under it. Shared by the portal Ask
 * panel and the admin "Try it" panel so both render an answer the same way.
 *
 * Chips and cards come only from `done.citations`: while the answer streams the
 * markers are taken out (the streamed text is unsanitised and may name a source
 * the final answer drops), and a marker the final answer does not cite is too.
 *
 * Knowledge wave K2 (the manager's team answers): a `[T1]` marker cites team
 * data — its chip reads "Team · Training status · as of 4 Oct" and opens
 * nothing, and its visible label is "T1" with the people icon, in the text
 * and under the answer, so it never reads like document "1" (knowledge polish,
 * 2026-10-04: one answer showed a team "1" beside a document "1"); a person's name arrives as `[Name](#person)` and is shown as typed
 * text (<UserText>); a drafted reminder arrives in a ```draft fence and is
 * shown as a block with a Copy button (nothing is ever sent).
 */

const MARKER = /\[([ST]\d+)\]/g
const CITE_PREFIX = '#hf-cite-'
/** The link the manager assistant writes a person's name as (the API's KNOWLEDGE_TEAM_PERSON_HREF). */
export const PERSON_HREF = '#person'
const PERSON_LINK = /\[([^\]\n]{1,200})\]\(#person\)/g
/** A drafted reminder: a ```draft fence (the API's KNOWLEDGE_TEAM_DRAFT_FENCE); an unclosed one runs to the end while it streams. */
const DRAFT = /```draft[^\n]*\n([\s\S]*?)(?:```|$)/g

/**
 * The answer's prose. Each paragraph, item and heading takes the direction of
 * its own first letter (`unicode-bidi: plaintext`), as the question box does:
 * an English answer in an Arabic drawer read right to left with its full stop
 * at the wrong end (".encrypted Group USB drive from IT through the Service
 * Desk"), and an Arabic answer in an English one the other way round.
 */
export const ANSWER_PROSE = 'text-sm [&_:is(p,li,h1,h2,h3,h4,blockquote)]:[unicode-bidi:plaintext]'

export type AnswerSegment = { kind: 'text'; text: string } | { kind: 'draft'; text: string }

/**
 * The answer cut into its prose and its drafted reminders. A draft is the
 * message as the manager will send it: names as plain text, no source marker.
 */
export function splitDrafts(text: string): AnswerSegment[] {
  const segments: AnswerSegment[] = []
  let at = 0
  for (const match of text.matchAll(DRAFT)) {
    const before = text.slice(at, match.index)
    if (before.trim()) segments.push({ kind: 'text', text: before })
    const draft = match[1]
      .replace(PERSON_LINK, '$1')
      .replace(/[ \t]*\[[ST]\d+\]/g, '')
      .replace(/[ \t]+\n/g, '\n')
      .trim()
    if (draft) segments.push({ kind: 'draft', text: draft })
    at = (match.index ?? 0) + match[0].length
  }
  const rest = text.slice(at)
  if (rest.trim() || segments.length === 0) segments.push({ kind: 'text', text: rest })
  return segments
}

/** The text with each cited marker as a link the renderer hands back to us, the rest removed. */
export function withCitationLinks(text: string, citations: Citation[]): string {
  const cited = new Set(citations.map((c) => c.marker))
  return text.replace(MARKER, (_match, marker: string) => (cited.has(marker) ? `[${marker}](${CITE_PREFIX}${marker})` : ''))
}

const markerNumber = (marker: string) => marker.replace(/^[ST]/i, '')

/** The cited sources, one each: the documents in the order the answer numbers them, then the team data. */
function uniqueCitations(citations: Citation[]): Citation[] {
  const seen = new Set<string>()
  return citations
    .filter((c) => (seen.has(c.marker) ? false : (seen.add(c.marker), true)))
    .sort(
      (a, b) =>
        Number(isTeamCitation(a)) - Number(isTeamCitation(b)) || Number(markerNumber(a.marker)) - Number(markerNumber(b.marker)),
    )
}

const DATASET_KEYS: Record<KnowledgeTeamDataset, string> = {
  overview: 'knowledgePortal.team.datasets.overview',
  training: 'knowledgePortal.team.datasets.training',
  simulations: 'knowledgePortal.team.datasets.simulations',
  risk: 'knowledgePortal.team.datasets.risk',
}

/** A team marker's visible label, "T1" in English: never just the number a document marker shows. */
function useTeamMarkerLabel() {
  const { t } = useTranslation()
  return (citation: TeamCitation): string => t('knowledgePortal.team.marker', { number: markerNumber(citation.marker) })
}

/** "Team · Training status · as of 4 Oct": the day the data was read, in the workspace's zone, in the reader's language. */
function useTeamChipLabel() {
  const { t, locale } = useTranslation()
  return (citation: TeamCitation): string => {
    const day = formatMomentIn(citation.asOf, citation.timeZone, 'yyyy-MM-dd') || citation.asOf.slice(0, 10)
    const date = formatLearnerDay(day, locale, { day: 'numeric', month: 'short' })
    return t('knowledgePortal.team.chip', { dataset: t(DATASET_KEYS[citation.dataset]), date })
  }
}

export interface AnswerViewProps {
  turn: AskTurn
  escalation: KnowledgeEscalation | null
  onOpenSource: (citation: DocumentCitation) => void
  /** Where the policy library is, for the states that point to it; null on the admin preview. */
  libraryHref: string | null
}

export function AnswerView({ turn, escalation, onOpenSource, libraryHref }: AnswerViewProps) {
  const { t } = useTranslation()

  if (turn.status === 'failed' && turn.failure) {
    return <AskStateCard failure={turn.failure} libraryHref={libraryHref} />
  }

  if (turn.status === 'streaming') {
    if (!turn.text) {
      return (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          {t('knowledgePortal.panel.thinking')}
        </p>
      )
    }
    return <AnswerBody text={turn.text} citations={[]} />
  }

  if (turn.outcome === 'not_found') {
    return <NotFoundCard content={turn.text} escalation={escalation} />
  }

  const sources = uniqueCitations(turn.citations)
  const documents = sources.filter((c): c is DocumentCitation => !isTeamCitation(c))
  const team = sources.filter(isTeamCitation)

  return (
    <div className="space-y-3">
      <AnswerBody text={turn.text} citations={sources} onOpenSource={onOpenSource} />
      {sources.length > 0 ? (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">{t('knowledgePortal.answer.sources')}</p>
          {documents.length > 0 ? (
            <ul className="grid gap-2">
              {documents.map((citation) => (
                <li key={citation.marker}>
                  <SourceCard citation={citation} onOpen={onOpenSource} />
                </li>
              ))}
            </ul>
          ) : null}
          {team.length > 0 ? (
            <ul className="flex flex-wrap gap-2">
              {team.map((citation) => (
                <li key={citation.marker}>
                  <TeamSource citation={citation} />
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/**
 * The answer's text: prose through the markdown renderer (markers as chips,
 * names as typed text) and each drafted reminder as a block to copy.
 */
function AnswerBody({
  text,
  citations,
  onOpenSource,
}: {
  text: string
  citations: Citation[]
  onOpenSource?: (citation: DocumentCitation) => void
}) {
  const chipFor = (href: string | undefined, children: ReactNode): ReactNode | null => {
    if (href === PERSON_HREF) return <UserText>{children}</UserText>
    if (!href?.startsWith(CITE_PREFIX)) return null
    const citation = citations.find((c) => c.marker === href.slice(CITE_PREFIX.length))
    if (!citation) return null
    if (isTeamCitation(citation)) return <TeamChip citation={citation} />
    return onOpenSource ? <SourceChip citation={citation} onOpen={onOpenSource} /> : null
  }
  return (
    <>
      {splitDrafts(text).map((segment, i) =>
        segment.kind === 'draft' ? (
          <DraftBlock key={i} text={segment.text} />
        ) : (
          <MarkdownRenderer key={i} content={withCitationLinks(segment.text, citations)} renderLink={chipFor} className={ANSWER_PROSE} />
        ),
      )}
    </>
  )
}

/** A `[T1]` marker in the text: a small chip that opens nothing; a screen reader hears what it cites. */
function TeamChip({ citation }: { citation: TeamCitation }) {
  const label = useTeamChipLabel()(citation)
  const marker = useTeamMarkerLabel()(citation)
  return (
    <span
      title={label}
      className="mx-0.5 inline-flex h-5 min-w-5 items-center justify-center gap-0.5 rounded-full bg-muted px-1.5 align-super text-[10px] font-semibold leading-none text-muted-foreground"
    >
      <Users className="size-2.5" aria-hidden="true" />
      <span aria-hidden="true">{marker}</span>
      <span className="sr-only">{label}</span>
    </span>
  )
}

/** A team source under the answer: "Team · Training status · as of 4 Oct". Not a link: team data opens nothing. */
function TeamSource({ citation }: { citation: TeamCitation }) {
  const label = useTeamChipLabel()(citation)
  const marker = useTeamMarkerLabel()(citation)
  return (
    <span className="inline-flex items-center gap-2 rounded-full border bg-background px-3 py-1.5 text-xs text-muted-foreground">
      <span
        aria-hidden="true"
        className="inline-flex h-5 min-w-5 items-center justify-center gap-0.5 rounded-full bg-muted px-1.5 text-[10px] font-semibold"
      >
        <Users className="size-2.5" aria-hidden="true" />
        <span>{marker}</span>
      </span>
      <span>{label}</span>
    </span>
  )
}

/** A drafted reminder: the text as the manager will send it, and a Copy button. Nothing is ever sent from here. */
function DraftBlock({ text }: { text: string }) {
  const { t } = useTranslation()
  const titleId = useId()
  const [copy, setCopy] = useState<'idle' | 'copied' | 'failed'>('idle')
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopy('copied')
    } catch {
      setCopy('failed')
    }
  }
  return (
    <section aria-labelledby={titleId} className="space-y-2 rounded-lg border bg-muted/40 p-3">
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <h4 id={titleId} className="text-xs font-semibold">
          {t('knowledgePortal.team.draft.title')}
        </h4>
        <Button type="button" variant="outline" size="sm" className="ms-auto h-7 gap-1.5 px-2 text-xs" onClick={() => void onCopy()}>
          {copy === 'copied' ? <Check className="size-3.5" aria-hidden="true" /> : <Copy className="size-3.5" aria-hidden="true" />}
          {copy === 'copied' ? t('knowledgePortal.team.draft.copied') : t('knowledgePortal.team.draft.copy')}
        </Button>
      </div>
      <UserText className="block whitespace-pre-wrap text-sm">{text}</UserText>
      <p className="text-xs text-muted-foreground" aria-live="polite">
        {copy === 'failed' ? t('knowledgePortal.team.draft.copyFailed') : t('knowledgePortal.team.draft.notSent')}
      </p>
    </section>
  )
}

function SourceChip({ citation, onOpen }: { citation: DocumentCitation; onOpen: (c: DocumentCitation) => void }) {
  const { t } = useTranslation()
  const number = markerNumber(citation.marker)
  const label =
    citation.pageFrom !== null
      ? t('knowledgePortal.answer.sourceChipPage', { number, title: citation.title, page: citation.pageFrom })
      : t('knowledgePortal.answer.sourceChip', { number, title: citation.title })
  return (
    <Button
      type="button"
      variant="ghost"
      size="xs"
      aria-label={label}
      title={label}
      onClick={() => onOpen(citation)}
      className="mx-0.5 h-5 min-w-5 rounded-full bg-primary/10 px-1.5 align-super text-[10px] leading-none font-semibold text-primary hover:bg-primary/20 hover:text-primary dark:hover:bg-primary/25"
    >
      {number}
    </Button>
  )
}

function SourceCard({ citation, onOpen }: { citation: DocumentCitation; onOpen: (c: DocumentCitation) => void }) {
  const pageLabel = usePageLabel()
  const where = pageLabel(citation.pageFrom, citation.pageTo)
  return (
    <Button
      type="button"
      variant="outline"
      onClick={() => onOpen(citation)}
      className="h-auto w-full items-start justify-start gap-3 rounded-lg p-3 text-start font-normal whitespace-normal shadow-none"
    >
      <span
        aria-hidden="true"
        className="mt-0.5 inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 px-1.5 text-[10px] font-semibold text-primary"
      >
        {markerNumber(citation.marker)}
      </span>
      <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span className="min-w-0 flex-1">
        <UserText className="block text-sm font-medium">{citation.title}</UserText>
        {citation.heading ? <UserText className="block text-xs text-muted-foreground">{citation.heading}</UserText> : null}
        {where ? <span className="block text-xs text-muted-foreground">{where}</span> : null}
      </span>
    </Button>
  )
}

const safeUrl = (url: string | null) => (url && /^https?:\/\//i.test(url) ? url : null)

function NotFoundCard({ content, escalation }: { content: string; escalation: KnowledgeEscalation | null }) {
  const { t } = useTranslation()
  const titleId = useId()
  const url = safeUrl(escalation?.url ?? null)
  const hasContact = !!(escalation && (escalation.label || escalation.email || url))
  return (
    <section aria-labelledby={titleId} className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
      <div className="flex items-start gap-3">
        <SearchX className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
        <div className="min-w-0 flex-1 space-y-2">
          <h3 id={titleId} className="text-sm font-semibold">
            {t('knowledgePortal.notFound.title')}
          </h3>
          {content ? <MarkdownRenderer content={withCitationLinks(content, [])} className={ANSWER_PROSE} /> : null}
          {hasContact && escalation ? (
            <div className="space-y-1 rounded-lg bg-background/60 p-3 text-sm">
              <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <LifeBuoy className="h-3.5 w-3.5" aria-hidden="true" />
                {t('knowledgePortal.notFound.contact')}
              </p>
              {escalation.label ? <UserText className="block font-medium">{escalation.label}</UserText> : null}
              {escalation.email ? (
                <a href={`mailto:${escalation.email}`} dir="ltr" className="block text-primary underline-offset-2 hover:underline">
                  {escalation.email}
                </a>
              ) : null}
              {url ? (
                <a href={url} target="_blank" rel="noopener noreferrer" className="block text-primary underline-offset-2 hover:underline">
                  {t('knowledgePortal.notFound.link')}
                </a>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  )
}

const FAILURE_ICON: Record<AskFailure, typeof Info> = {
  disabled: Ban,
  limit: Hourglass,
  tooLong: Info,
  unavailable: TriangleAlert,
  conversationNotFound: Info,
  moduleDisabled: Ban,
  allowance: Hourglass,
  rateLimited: Clock,
  signedOut: LogIn,
  generic: TriangleAlert,
}

/** A designed state in place of an answer: a title, a sentence, and where to go next. */
export function AskStateCard({ failure, libraryHref }: { failure: AskFailure; libraryHref: string | null }) {
  const { t } = useTranslation()
  const titleId = useId()
  const Icon = FAILURE_ICON[failure]
  const keys = ASK_FAILURE_KEYS[failure]
  const pointToLibrary = failure === 'disabled' || failure === 'allowance' || failure === 'limit'
  const serious = failure === 'unavailable' || failure === 'generic'
  return (
    <section
      aria-labelledby={titleId}
      className={cn(
        'rounded-xl border p-4',
        serious ? 'border-destructive/30 bg-destructive/5' : 'border-border bg-muted/40',
      )}
    >
      <div className="flex items-start gap-3">
        <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', serious ? 'text-destructive' : 'text-muted-foreground')} aria-hidden="true" />
        <div className="min-w-0 flex-1 space-y-1">
          <h3 id={titleId} className="text-sm font-semibold">
            {t(keys.title)}
          </h3>
          <p className="text-sm text-muted-foreground">{t(keys.body)}</p>
          {libraryHref && pointToLibrary ? (
            <Link href={libraryHref} className="inline-block pt-1 text-sm font-medium text-primary underline-offset-2 hover:underline">
              {t('knowledgePortal.panel.library')}
            </Link>
          ) : null}
        </div>
      </div>
    </section>
  )
}
